/**
 * currentTurnCaption — pure caption-state derivation for the Halo
 * current-turn caption strip. Kept out of `FounderConsole` so it can be
 * unit-tested without mounting the console or wiring up auth/session.
 *
 * Rules (Root remainder C):
 *   • Only the LATEST turn drives the caption.
 *   • If the latest turn is an assistant turn, its full
 *     `normalized ?? raw` text is returned as `visualText`.
 *     Long text is NEVER discarded — the whole normalized turn is
 *     available; the visible container is expected to scroll.
 *   • If the latest turn is a user turn, all caption fields clear —
 *     a newer user turn strictly supersedes any prior assistant text.
 *   • `completedText` / `completedTurnId` are populated only when the
 *     assistant turn's `status` is `"completed"` (turns without an
 *     explicit status default to completed, matching
 *     `flattenHistoryToTurns` behavior).
 *   • Empty / whitespace-only text yields empty caption fields even
 *     for a completed assistant turn.
 */

export function computeCaptionState(turns) {
    if (!Array.isArray(turns) || turns.length === 0) {
        return { visualText: "", completedText: "", completedTurnId: null };
    }
    const latest = turns[turns.length - 1];
    if (!latest || latest.role !== "assistant") {
        return { visualText: "", completedText: "", completedTurnId: null };
    }
    const rawText = String(latest.normalized ?? latest.raw ?? "").trim();
    if (!rawText) {
        return { visualText: "", completedText: "", completedTurnId: null };
    }
    const isCompleted = (latest.status ?? "completed") === "completed";
    return {
        visualText: rawText,
        completedText: isCompleted ? rawText : "",
        completedTurnId: isCompleted ? (latest.id ?? null) : null,
    };
}

export default computeCaptionState;
